import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import LoadingIndicator from '@/src/Common/components/LoadingIndicator';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import useAssets from './hooks/useAssets';

const DropZone = styled.div<{ isDragging: boolean }>`
    width: 200px;
    height: 200px;
    border: 2px dashed ${(props) => (props.isDragging ? '#2196f3' : '#ccc')};
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    position: relative;
    background: ${(props) =>
        props.isDragging ? 'rgba(33, 150, 243, 0.1)' : 'transparent'};
    transition: all 0.3s ease;
`;

const ImagePreview = styled.img<{ isHovering: boolean }>`
    max-width: 200px;
    max-height: 200px;
    object-fit: contain;
    background-color: #ffffff;
    background-image: linear-gradient(45deg, #dbdbdb 25%, transparent 25%),
        linear-gradient(-45deg, #dbdbdb 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, #dbdbdb 75%),
        linear-gradient(-45deg, transparent 75%, #dbdbdb 75%);
    background-size: 20px 20px;
    background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
    transform: scale(0.9);
    transition: transform 0.3s ease;
`;

const ImageDimensions = styled.p`
    font-size: 10px;
    color: #000;
    margin-top: 8px;
`;

export default function ImageInput({
    image,
    onImageChange,
}: {
    image: string | null;
    onImageChange: (image: string | null) => void;
}) {
    const { uploadAsset } = useAssets();
    const { orgId } = useUserOrg();
    const [isUploading, setIsUploading] = useState(false);
    const [failed, setFailed] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const [imageDimensions, setImageDimensions] = useState<{
        width: number;
        height: number;
    } | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [value, setValue] = useState(image);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);

        const file = e.dataTransfer.files[0];
        if (file && file.type.startsWith('image/')) {
            handleImageUpload(file);
        }
    };

    const handleImageUpload = async (file: File) => {
        setIsUploading(true);
        setFailed(false);
        try {
            const result = await uploadAsset({
                // @ts-ignore
                asset: file,
                name: file.name,
                folder: 'customizer',
                orgId: orgId.toString(),
            });

            const img = new Image();
            img.onload = async () => {
                setImageDimensions({
                    width: img.width,
                    height: img.height,
                });
                setIsUploading(false);
                setValue(result.url);
            };
            img.src = result.url;
        } catch {
            setIsUploading(false);
            setFailed(true);
        }
    };

    useEffect(() => {
        if (value) {
            const img = new Image();
            img.onload = () => {
                setImageDimensions({
                    width: img.width,
                    height: img.height,
                });
            };
            img.src = value;
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    useEffect(() => {
        onImageChange(value);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && file.type.startsWith('image/')) {
            handleImageUpload(file);
        }
    };

    return (
        <div>
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept="image/*"
                onChange={handleFileInput}
            />
            <DropZone
                isDragging={isDragging}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleClick}
            >
                {isUploading ? (
                    <LoadingIndicator />
                ) : failed ? (
                    <>
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            className="text-gray-500 dark:text-white"
                        >
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="17 8 12 3 7 8" />
                            <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        <p className="text-sm text-red-500 text-center">
                            Upload failed. Check your network and try again.
                        </p>
                    </>
                ) : value ? (
                    <ImagePreview
                        src={value}
                        isHovering={isHovering}
                        onMouseEnter={() => setIsHovering(true)}
                        onMouseLeave={() => setIsHovering(false)}
                    />
                ) : (
                    <>
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            className="text-gray-500 dark:text-white"
                        >
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="17 8 12 3 7 8" />
                            <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        <p className="text-sm text-gray-500 dark:text-white text-center">
                            Drop image here or click to upload
                        </p>
                    </>
                )}
            </DropZone>
            {imageDimensions && (
                <ImageDimensions>
                    Dimensions: {imageDimensions.width}×{imageDimensions.height}
                </ImageDimensions>
            )}
        </div>
    );
}
